import { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles } from 'vopay';
import { Button, CircleButton, Dialog, MaterialSymbol } from 'UI';
import styles from './IntegrationInstructionsDialog.module.scss';

const cn = withStyles(styles);

type IntegrationInstructionsDialogProps = {
  icon: ComponentProps<typeof MaterialSymbol>['icon'];
  title: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
};

/**
 * The Figma "Modal" chrome the integration instructions share: header bar with an icon, title and
 * close button, a scrolling body, and a full-width Continue Setup that returns to the drawer.
 */
export default function IntegrationInstructionsDialog({
  icon,
  title,
  isOpen,
  onClose,
  children,
}: IntegrationInstructionsDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Integrations.IntegrationInstructionsDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Dialog.Content closeOnEscape className={cn('dialog')}>
        <div className={cn('modal')} data-testid="integration-how-it-works">
          <div className={cn('header')}>
            <MaterialSymbol icon={icon} size="24px" className={cn('headerIcon')} />

            <Dialog.Title level="4" className={cn('title')}>
              {title}
            </Dialog.Title>

            <CircleButton
              icon="close"
              size="large"
              circleFill
              aria-label={tGlobal('Buttons.close')}
              onClick={onClose}
            />
          </div>

          <div className={cn('body')}>{children}</div>

          <div className={cn('footer')}>
            <Button colorScheme="primary" className={cn('continue')} onClick={onClose}>
              {t('continueSetup', { defaultValue: 'Continue Setup' })}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
